import { collection, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase/firebase";
import { submitTailorQuoteRequest } from "./tailorQuoteService";

function requireUser() {
  const user = auth.currentUser;
  if (!user) throw new Error("Please sign in to place your order.");
  return user;
}

async function writeOrder(payload) {
  const user = requireUser();
  const reference = doc(collection(db, "orders"));
  await setDoc(reference, {
    ...payload,
    orderId: reference.id,
    customerId: user.uid,
    userId: user.uid,
    customerEmail: payload.customerEmail || user.email || "",
    status: "pending",
    orderStatus: "pending",
    paymentStatus: payload.paymentStatus || "pending",
    assignedArtisanId: null,
    assignedArtisanAlias: null,
    customerRating: null,
    artisanRating: null,
    isTestOrder: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    source: "customer_web",
  });
  return reference.id;
}

export function placeStitchingOrder({ draft, garment, customer, knownTotal, conditional, pricing }) {
  const sourceDraft = draft || {};
  const delivery = sourceDraft.delivery || {};
  const measurements = sourceDraft.measurements || {};
  const fabric = sourceDraft.fabric || {};
  const selectedAddOns = sourceDraft.selectedAddOns || sourceDraft.addOns?.selected || [];
  const manualMeasurements = measurements.manualValues ?? measurements.values ?? sourceDraft.manualMeasurements ?? {};
  const policy = garment?.pricingPolicy || garment?.pricePolicy || {};

  return writeOrder({
    customerName: customer.name,
    customerPhone: customer.phone,
    customerEmail: customer.email,
    orderType: "stitching",
    serviceType: "stitching",
    workflowType: "stitching",
    garment: {
      id: garment?.id || sourceDraft.garmentId || "",
      name: garment?.name || sourceDraft.garmentName || "Garment",
      category: garment?.categoryId || "",
      image: garment?.image || garment?.mainImage || "",
      serviceType: "stitching",
      workflowType: "stitching",
      pricingPolicy: policy,
    },
    design: {
      imageUrl: sourceDraft.design?.imageUrl || sourceDraft.imageUrl || "",
      storagePath: sourceDraft.design?.storagePath || "",
      designId: sourceDraft.designId || sourceDraft.design?.id || "",
      name: sourceDraft.designName || sourceDraft.design?.name || "",
    },
    measurements: {
      method: measurements.method || "",
      selectedSize: measurements.selectedSize || "",
      manualMeasurements,
      measurementUnit: "inches",
    },
    fabric: fabric || { option: "not_selected" },
    addOns: {
      selected: selectedAddOns,
      snapshots: sourceDraft.addOnSnapshots || [],
      specialInstructions: sourceDraft.specialInstructions || "",
    },
    pickup: {
      method: delivery.pickupMethod || "",
      requiresInspection: conditional,
      requiresInspectionPhotos: policy.requiresInspectionPhotos === true,
      paymentCollectionStage: conditional ? "pickupAfterConfirmation" : "checkout",
    },
    delivery: {
      method: delivery.deliveryMethod || "",
      preferredDate: delivery.preferredDate || "",
      preferredTimeSlot: delivery.preferredTimeSlot || "",
      addressId: delivery.addressId || "",
      addressSnapshot: delivery.addressSnapshot || null,
      instructions: delivery.instructions || "",
    },
    basePrice: Number(sourceDraft.design?.basePrice ?? sourceDraft.basePrice ?? garment?.startingPrice ?? garment?.basePrice ?? knownTotal ?? 0),
    total: Number(knownTotal || 0),
    pricing: pricing ? { ...pricing, grandTotal: Number(pricing.grandTotal || knownTotal || 0) } : { grandTotal: Number(knownTotal || 0) },
    priceBreakdown: pricing || { grandTotal: Number(knownTotal || 0) },
    paymentMethod: conditional ? "pickup_after_confirmation" : "online",
    paymentStatus: conditional ? "blocked_until_confirmation" : "pending",
    notes: conditional ? "Conditional order. Final price and payment are confirmed during pickup." : "",
  });
}

export function placeWorkflowOrder({ draft, paymentMethod }) {
  const item = draft.item || {};
  const details = draft.details || {};
  const pricing = draft.pricing || {};
  const workflow = draft.workflow || "service";
  const isHomeMeasurement = workflow === "home-measurement";
  return writeOrder({
    customerName: details.name || "",
    customerPhone: details.phone || "",
    orderType: isHomeMeasurement ? "home_measurement_visit" : workflow,
    serviceType: isHomeMeasurement ? "home_measurement_visit" : workflow,
    workflowType: isHomeMeasurement ? "stitching" : workflow,
    garment: {
      id: item.id || "",
      name: item.name || item.serviceName || "StichB service",
      image: item.image || item.imageUrl || item.mainImage || "",
      serviceType: workflow,
      workflowType: workflow,
    },
    design: { source: workflow },
    measurements: { method: workflow === "home-measurement" ? "home_measurement_visit" : "not_applicable" },
    fabric: workflow === "fabric-store" ? { option: "buy_from_stichb", ...details } : { option: "not_applicable" },
    addOns: { selected: [], specialInstructions: details.notes || "" },
    pickup: { method: workflow === "home-measurement" ? "home_visit" : "home_pickup" },
    delivery: {
      method: workflow === "home-measurement" ? "home_visit" : "home_delivery",
      preferredDate: details.date || "",
      preferredTimeSlot: details.timeSlot || "",
      addressSnapshot: {
        recipientName: details.name || "",
        phone: details.phone || "",
        addressLine1: details.address || "",
        city: details.city || "",
        state: details.state || "",
        postalCode: details.postalCode || "",
      },
    },
    items: [{
      itemId: item.id || "",
      itemType: workflow,
      itemName: item.name || item.serviceName || "StichB service",
      imageUrl: item.image || item.imageUrl || "",
      quantity: Number(details.quantity || 1),
      unitPrice: Number(pricing.unitPrice || 0),
      subtotal: Number(pricing.total || 0),
    }],
    basePrice: Number(pricing.unitPrice || pricing.total || 0),
    total: Number(pricing.total || 0),
    pricing: { ...pricing, grandTotal: Number(pricing.total || 0) },
    ...(isHomeMeasurement ? { homeMeasurement: { tentativePrice: Number(pricing.total || 149), finalPriceStatus: "to_be_confirmed_by_admin", pricingConfigDoc: "service_pricing/home_measurement_visit" } } : {}),
    paymentMethod,
    paymentStatus: "pending",
    notes: details.notes || "",
  });
}

export function placeFabricOrder({ item, fabric, deliveryFee = 0, discount = 0 }) {
  const subtotal = Number(fabric.subtotal || 0);
  const total = Math.max(0, subtotal + Number(deliveryFee || 0) - Number(discount || 0));
  const deliveryDate = new Date();
  deliveryDate.setDate(deliveryDate.getDate() + 1);
  deliveryDate.setHours(0, 0, 0, 0);
  const selectedOptions = {
    colourPreference: fabric.colourPreference || "original",
    ...(fabric.colourPreference === "custom" && fabric.color ? { colour: fabric.color } : {}),
    ...(fabric.colourPreference === "custom" && fabric.colorHex ? { colourHex: fabric.colorHex } : {}),
    ...(fabric.colourPreference === "custom" && Array.isArray(fabric.colorSelections) ? { colourSelections: fabric.colorSelections } : {}),
    ...(fabric.colorPartsSource ? { colourPartsSource: fabric.colorPartsSource } : {}),
    ...(fabric.colorPartsDesignName ? { colourPartsDesignName: fabric.colorPartsDesignName } : {}),
    ...(fabric.pattern ? { pattern: fabric.pattern } : {}),
    basePriceUnit: item.basePriceUnit || "",
  };
  return writeOrder({
    customerName: fabric.name || "", customerPhone: fabric.phone || "",
    orderType: "fabric_sale", serviceType: "fabricSale", workflowType: "fabricSale",
    checkoutId: `fabric_${item.id || "item"}_${Date.now()}`, status: "pending",
    garment: { id: item.id || "", name: item.name || "Fabric", image: item.image || item.imageUrl || "", serviceType: "fabricSale", workflowType: "fabricSale" },
    design: { source: "not_applicable" }, measurements: { method: "not_applicable" },
    fabric: { option: "buy_from_stichb", selectedOptions },
    addOns: { selected: [], specialInstructions: fabric.note || "" }, pickup: { method: "not_applicable" },
    items: [{ itemId: item.id || "", itemType: "fabric", itemName: item.name || "Fabric", imageUrl: item.image || item.imageUrl || "", quantity: Number(fabric.quantity || 0), unit: fabric.unit || item.basePriceUnit || "", unitPrice: Number(fabric.quantity || 0) > 0 ? subtotal / Number(fabric.quantity) : 0, subtotal, selectedOptions, specialInstructions: fabric.note || "" }],
    delivery: { fullName: fabric.name || "", phoneNumber: fabric.phone || "", address: [fabric.line1, fabric.line2].filter(Boolean).join(", "), city: fabric.city || "", state: fabric.state || "", postalCode: fabric.pin || "", slotId: fabric.slot || "", slotLabel: fabric.slot || "", deliveryDate, deliveryType: "home_delivery", instructions: fabric.note || "", addressSnapshot: { recipientName: fabric.name || "", phone: fabric.phone || "", addressLine1: [fabric.line1, fabric.line2].filter(Boolean).join(", "), city: fabric.city || "", state: fabric.state || "", postalCode: fabric.pin || "" } },
    subtotal, deliveryFee: Number(deliveryFee || 0), discount: Number(discount || 0), total, basePrice: subtotal,
    pricing: { subtotal, deliveryFee: Number(deliveryFee || 0), discount: Number(discount || 0), grandTotal: total },
    paymentMethod: "Cash on Delivery", paymentStatus: "pending", notes: fabric.note || "",
  });
}

export function placeMaterialOrder({ cart, customer, paymentMethod = "Cash on Delivery" }) {
  const subtotal = cart.reduce((sum, line) => sum + Number(line.product.price || 0) * Number(line.quantity || 0), 0);
  const deliveryFee = subtotal >= 499 ? 0 : 49;
  const total = subtotal + deliveryFee;
  return writeOrder({
    customerName: customer.name.trim(), customerPhone: customer.phone.trim(), phone: customer.phone.trim(),
    orderType: "tailoring_materials", serviceType: "tailoringMaterials", workflowType: "tailoringMaterials", status: "pending",
    deliveryAddress: customer.address.trim(),
    garment: { name: cart.length === 1 ? cart[0].product.name : `${cart.length} tailoring material items`, image: cart.length === 1 ? (cart[0].product.image || "") : "", workflowType: "tailoringMaterials" },
    design: { source: "not_applicable" }, measurements: { method: "not_applicable" }, fabric: { option: "not_applicable" }, addOns: { selected: [] }, pickup: { method: "not_applicable" },
    delivery: { address: customer.address.trim(), method: "doorstep_delivery", addressSnapshot: { recipientName: customer.name.trim(), phone: customer.phone.trim(), addressLine1: customer.address.trim() } },
    items: cart.map((line) => ({ productId: line.product.id, itemId: line.product.id, name: line.product.name, itemName: line.product.name, itemType: "tailoring_material", imageUrl: line.product.image || "", quantity: Number(line.quantity), unit: line.product.unit || "Piece", unitPrice: Number(line.product.price || 0), subtotal: Number(line.product.price || 0) * Number(line.quantity) })),
    subtotal, deliveryFee, total, basePrice: total, pricing: { subtotal, deliveryFee, grandTotal: total }, paymentMethod, paymentStatus: "pending",
  });
}

export function placeMachineServiceOrder({ service, booking }) {
  const appointmentDate = new Date(`${booking.date}T00:00:00`);
  const price = Number(service.price ?? service.basePrice ?? 0);
  return writeOrder({
    customerName: booking.name || "", customerPhone: booking.phone || "",
    orderType: "sewing_machine_service", serviceType: "sewingMachineService", workflowType: "sewingMachineService", status: "pending",
    serviceId: service.id || "", serviceName: service.name || "Machine service",
    garment: { id: service.id || "", name: service.name || "Machine service", image: service.image || "", workflowType: "sewingMachineService" },
    design: { source: "not_applicable" }, measurements: { method: "not_applicable" }, fabric: { option: "not_applicable" }, addOns: { selected: [] }, pickup: { method: "technician_home_visit" },
    machineType: booking.machineType, reportedIssues: booking.reportedIssues, notes: booking.notes.trim(), serviceAddress: booking.address.trim(),
    delivery: { address: booking.address.trim(), method: "technician_home_visit", preferredDate: appointmentDate, preferredTimeSlot: booking.slot, addressSnapshot: { recipientName: booking.name || "", phone: booking.phone || "", addressLine1: booking.address.trim() } },
    appointmentDate, appointmentSlot: booking.slot,
    items: [{ itemId: service.id || "", itemType: "machine_service", itemName: service.name || "Machine service", quantity: 1, unitPrice: price, subtotal: price }],
    subtotal: price, total: price, basePrice: price, pricing: { subtotal: price, grandTotal: price }, paymentMethod: "Pay after service", paymentStatus: "pending",
  });
}

export function placeCostumeOrder({ costume, renting, details, pricing }) {
  return writeOrder({
    customerName: details.name || "",
    customerPhone: details.phone || "",
    orderType: renting ? "costume_rental" : "costume_purchase",
    serviceType: renting ? "costume_rental" : "costume_purchase",
    workflowType: "costume_rentals",
    status: "pending",
    costumeId: costume.id,
    costumeName: costume.name,
    garment: { id: costume.id, name: costume.name, image: costume.imageUrls?.[0] || "", workflowType: "costume_rentals" },
    design: { source: "costume_catalog" }, measurements: { method: "standard_size", selectedSize: details.size },
    fabric: { option: "included" }, addOns: { selected: [] }, pickup: { method: renting ? "return_pickup" : "not_applicable" },
    delivery: { method: "home_delivery", preferredDate: details.deliveryDate, preferredTimeSlot: details.deliverySlot, addressSnapshot: { recipientName: details.name, phone: details.phone, addressLine1: details.address } },
    returnPickupDate: renting ? details.returnDate : null,
    returnPickupSlot: renting ? details.returnSlot : null,
    rentalDays: renting ? pricing.days : 0,
    securityDeposit: renting ? pricing.deposit : 0,
    subtotal: pricing.subtotal, total: pricing.total, basePrice: pricing.total,
    pricing: { ...pricing, grandTotal: pricing.total },
    policyConsent: renting ? details.consent : null,
    paymentMethod: "UPI", paymentStatus: "pending",
  });
}

export function placeAlterationOrder({ draft }) {
  const sourceDraft = draft || {};
  const services = Array.isArray(sourceDraft.selectedServices)
    ? sourceDraft.selectedServices
    : Array.isArray(sourceDraft.services)
      ? sourceDraft.services
      : [];
  const details = sourceDraft.details || {};
  const pickup = sourceDraft.pickup || {};
  const startingPrice = services.reduce(
    (total, item) => total + Number(item.minimumPrice || item.price || 0),
    0,
  );

  return submitTailorQuoteRequest({
    customer: {
      name: pickup.name || "",
      phone: pickup.phone || "",
      email: pickup.email || "",
    },
    garmentName:
      sourceDraft.garment?.name ||
      sourceDraft.garmentName ||
      "Alteration request",
    garmentId: sourceDraft.garment?.id || sourceDraft.garmentId || "",
    serviceType: "alteration",
    quotationType: "alteration",
    startingPrice,
    designImageDataUrl: details.photoUrls?.[0] || "",
    designFileName: details.photoNames?.[0] || "alteration-reference",
    notes: details.notes || "",
    requestDetails: sourceDraft,
    ...(sourceDraft.quoteContact || {}),
  });
}

export function placeDryCleaningOrder({ basket, schedule, totals }) {
  const first = basket[0];
  const slotLabel = (slot) => slot?.slotName ? `${slot.slotName} · ${slot.startTime}–${slot.endTime}` : `${slot?.startTime || ""}–${slot?.endTime || ""}`;
  return writeOrder({
    customerName: schedule.name, customerPhone: schedule.phone, orderType: "dry_cleaning", serviceType: "dryCleaning", workflowType: "dryCleaning", status: "pending",
    garment: { name: basket.length === 1 ? first.service.name : `${basket.length} garment-care services`, image: first?.service?.imageUrl || "", workflowType: "dryCleaning" },
    design: { source: "not_applicable" }, measurements: { method: "not_applicable" }, fabric: { option: "not_applicable" }, addOns: { selected: [], specialInstructions: schedule.notes || "" }, pickup: { method: "home_pickup" },
    delivery: { method: "home_delivery", address: schedule.address, pincode: schedule.pincode, pickupDate: new Date(`${schedule.pickupDate}T00:00:00`), pickupSlotId: schedule.pickupSlotId, pickupSlot: slotLabel(schedule.pickupSlot), deliveryDate: new Date(`${schedule.deliveryDate}T00:00:00`), deliverySlotId: schedule.deliverySlotId, deliverySlot: slotLabel(schedule.deliverySlot), preferredDate: schedule.pickupDate, preferredTimeSlot: slotLabel(schedule.pickupSlot), addressSnapshot: { recipientName: schedule.name, phone: schedule.phone, addressLine1: schedule.address, postalCode: schedule.pincode } },
    items: basket.map((line) => ({ serviceId: line.service.id, serviceName: line.service.name, categoryId: line.service.categoryId || "", categoryName: line.service.categoryName || "", quantity: Number(line.quantity || 1), priceType: line.pricing.priceType || line.service.priceType || "Per Piece", unitPrice: Number(line.pricing.basePrice || 0), express: line.express === true, expressCharge: line.express ? Number(line.pricing.expressCharge || 0) : 0 })),
    notes: schedule.notes || "", pricing: { servicesTotal: totals.services, expressCharge: totals.express, pickupCharge: totals.pickup, deliveryCharge: totals.delivery, grandTotal: totals.total }, basePrice: totals.total, total: totals.total, paymentMethod: "Cash on Delivery", paymentStatus: "pending",
  });
}
